import { useRef } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandLessIcon from '@mui/icons-material/ExpandLessRounded'
import HeadsetIcon from '@mui/icons-material/HeadsetRounded'
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next'

import { useOnMount } from 'hooks'

import {
  rootStyle,
  mainStyle,
  titleStyle,
  subtitleStyle,
  captionStyle,
  captionIconStyle,
  contentStyle,
} from './Song.styles'

function Song({ song, songType, songId, songArtist, songSubtitle }) {
  const { t } = useTranslation()

  const ref = useRef()

  const initialScrollEventRef = useRef(true)
  const scrollFrameComingRef = useRef(false)
  const prevScrollPosRef = useRef(0)

  useOnMount(() => {
    if (window) {
      const el = ref.current

      const onScroll = () => {
        if (initialScrollEventRef.current) {
          initialScrollEventRef.current = false
          return
        }

        if (!initialScrollEventRef.current && !scrollFrameComingRef.current) {
          window.requestAnimationFrame(() => {
            const currentScrollPos = window.pageYOffset

            if (Math.abs(currentScrollPos - prevScrollPosRef.current) > 20) {
              if (prevScrollPosRef.current < currentScrollPos) {
                el.style.transform = 'translateY(100%)'
                el.style.opacity = 0
              } else {
                el.style.transform = 'translateY(0)'
                el.style.opacity = 1
              }
            }

            if (currentScrollPos + window.innerHeight >= document.body.scrollHeight) {
              el.style.transform = 'translateY(0)'
              el.style.opacity = 1
            }

            scrollFrameComingRef.current = false
            prevScrollPosRef.current = currentScrollPos
          })

          scrollFrameComingRef.current = true
        }
      }

      window.addEventListener('scroll', onScroll)

      return () => window.removeEventListener('scroll', onScroll)
    }
  })

  let _iframe

  if (songType === 'soundcloud') {
    _iframe = (
      <iframe
        width="100%"
        height="100"
        scrolling="no"
        frameBorder="no"
        allow="autoplay"
        src={`https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/${songId}&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false`}
      />
    )
  } else if (songType === 'spotify') {
    _iframe = (
      <iframe
        src={`https://open.spotify.com/embed/track/${songId}`}
        width="100%"
        height="80"
        frameBorder="no"
        allow="encrypted-media"
        allowtransparency="true"
      />
    )
  }

  return (
    <Box sx={rootStyle} ref={ref}>
      <Accordion elevation={0} square sx={mainStyle}>
        <AccordionSummary expandIcon={<ExpandLessIcon />} sx={titleStyle}>
          <Box sx={captionStyle}>
            <HeadsetIcon fontSize="small" sx={captionIconStyle}/>
            <Typography variant="overline">
              {t('song.header')}
            </Typography>
          </Box>
          <Typography variant="body2">
            <Trans
              i18nKey="song.title"
              values={{ song, songArtist }}
              components={{ b: <strong /> }}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={contentStyle}>
          <Typography variant="body2" color="textSecondary" sx={subtitleStyle}>
            <em>{songSubtitle}</em>
          </Typography>
          {_iframe}
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

const { string } = PropTypes

Song.propTypes = {
  song: string,
  songId: string,
  songArtist: string,
  songSubtitle: string,
  songType: string
}

export default Song
